import { addressDisplay, isAddressLike } from "@/shared/lib/formatting";
import { humaniseBadge } from "@/admin/components/shared/detail-value-formatting";
import {
  entityFileDisplayName,
  formatEntityCardValue,
  isEntityFileObject,
} from "@/admin/lib/entity-card-display";

export interface HumaniseOptions {
  locale?: string;
  currency?: string;
}

function isMoneyObject(value: Record<string, unknown>): boolean {
  const keys = Object.keys(value);
  return keys.length > 0 && typeof value.currency === "string" && keys.every((k) => k === "amount" || k === "currency");
}

function formatMoneyString(
  value: Record<string, unknown>,
  locale?: string,
  currencyFallback?: string,
): string {
  const amount = value.amount;
  if (amount === null || amount === undefined || amount === "") return "";
  const num = typeof amount === "number" ? amount : Number(amount);
  if (Number.isNaN(num)) return "";
  const cur = (value.currency as string) || currencyFallback;
  if (!cur) return String(num);
  const hasDecimals = num % 1 !== 0;
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: cur,
      minimumFractionDigits: hasDecimals ? 2 : 0,
      maximumFractionDigits: hasDecimals ? 2 : 0,
    }).format(num);
  } catch {
    return `${cur} ${num}`;
  }
}

export function humanise(
  value: unknown,
  options?: HumaniseOptions,
): string {
  if (value === null || value === undefined || value === "") return "";
  if (Array.isArray(value)) {
    return value
      .map((part) => humanise(part, options))
      .filter(Boolean)
      .join(", ");
  }
  if (typeof value === "string") {
    if (value.includes(",")) {
      return value
        .split(",")
        .map((part) => humaniseBadge(part.trim()))
        .filter(Boolean)
        .join(", ");
    }
    return value
      .replace(/_/g, " ")
      .replace(
        /\w\S*/g,
        (part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase(),
      );
  }
  if (typeof value === "object") {
    const record = value as Record<string, unknown>;
    if (isAddressLike(record)) return addressDisplay(record);
    if (isMoneyObject(record))
      return formatMoneyString(record, options?.locale, options?.currency);
    if (isEntityFileObject(record))
      return entityFileDisplayName(record) || "-";
    return formatEntityCardValue(value);
  }
  return String(value);
}
